import { DifficultyLevel } from '../types'

export interface LevelConfig {
  id: DifficultyLevel
  label: string
  group: 6 | 7 | 8
  period: 'B' | 'M' | 'E' // Begin, Midden, Eind
  isPlus: boolean
  title: string // Full title for display
  hasContent?: boolean // Whether this level has question content available
}

export const LEVELS: LevelConfig[] = [
  // GROEP 6
  {
    id: 101,
    label: 'B6',
    group: 6,
    period: 'B',
    isPlus: false,
    title: 'Begin Groep 6',
    hasContent: true,
  },
  {
    id: 102,
    label: 'B6+',
    group: 6,
    period: 'B',
    isPlus: true,
    title: 'Begin Groep 6 (Plus)',
    hasContent: true,
  },
  {
    id: 103,
    label: 'M6',
    group: 6,
    period: 'M',
    isPlus: false,
    title: 'Midden Groep 6',
    hasContent: true,
  },
  {
    id: 104,
    label: 'M6+',
    group: 6,
    period: 'M',
    isPlus: true,
    title: 'Midden Groep 6 (Plus)',
    hasContent: true,
  },
  {
    id: 105,
    label: 'E6',
    group: 6,
    period: 'E',
    isPlus: false,
    title: 'Eind Groep 6',
    hasContent: true,
  },
  {
    id: 106,
    label: 'E6+',
    group: 6,
    period: 'E',
    isPlus: true,
    title: 'Eind Groep 6 (Plus)',
    hasContent: true,
  },

  // GROEP 7
  {
    id: 201,
    label: 'B7',
    group: 7,
    period: 'B',
    isPlus: false,
    title: 'Begin Groep 7',
    hasContent: true,
  },
  {
    id: 202,
    label: 'B7+',
    group: 7,
    period: 'B',
    isPlus: true,
    title: 'Begin Groep 7 (Plus)',
    hasContent: true,
  },
  {
    id: 203,
    label: 'M7',
    group: 7,
    period: 'M',
    isPlus: false,
    title: 'Midden Groep 7',
    hasContent: true,
  },
  {
    id: 204,
    label: 'M7+',
    group: 7,
    period: 'M',
    isPlus: true,
    title: 'Midden Groep 7 (Plus)',
    hasContent: true,
  },
  {
    id: 205,
    label: 'E7',
    group: 7,
    period: 'E',
    isPlus: false,
    title: 'Eind Groep 7',
    hasContent: true,
  },
  {
    id: 206,
    label: 'E7+',
    group: 7,
    period: 'E',
    isPlus: true,
    title: 'Eind Groep 7 (Plus)',
    hasContent: true,
  },

  // GROEP 8
  {
    id: 301,
    label: 'B8',
    group: 8,
    period: 'B',
    isPlus: false,
    title: 'Begin Groep 8',
    hasContent: true,
  },
  {
    id: 302,
    label: 'B8+',
    group: 8,
    period: 'B',
    isPlus: true,
    title: 'Begin Groep 8 (Plus)',
    hasContent: true,
  },
  {
    id: 303,
    label: 'M8',
    group: 8,
    period: 'M',
    isPlus: false,
    title: 'Midden Groep 8',
    hasContent: true,
  },
  {
    id: 304,
    label: 'M8+',
    group: 8,
    period: 'M',
    isPlus: true,
    title: 'Midden Groep 8 (Plus)',
    hasContent: true,
  },
  {
    id: 305,
    label: 'E8',
    group: 8,
    period: 'E',
    isPlus: false,
    title: 'Eind Groep 8',
    hasContent: true,
  },
  {
    id: 306,
    label: 'E8+',
    group: 8,
    period: 'E',
    isPlus: true,
    title: 'Eind Groep 8 (Plus)',
    hasContent: true,
  },
]

// Get all levels that have content available
export const getAvailableLevels = (): LevelConfig[] => {
  return LEVELS.filter((l) => l.hasContent)
}

// Get available levels for a specific group
export const getAvailableLevelsByGroup = (group: 6 | 7 | 8): LevelConfig[] => {
  return LEVELS.filter((l) => l.group === group && l.hasContent)
}

export const getLevelById = (id: DifficultyLevel): LevelConfig | undefined => {
  return LEVELS.find((l) => l.id === id)
}

export const getLevelsByGroup = (group: 6 | 7 | 8): LevelConfig[] => {
  return LEVELS.filter((l) => l.group === group)
}

export const getLevelLabel = (id: DifficultyLevel): string => {
  return getLevelById(id)?.label || `Lvl ${id}`
}

// Check if a level has content available
export const hasLevelContent = (id: DifficultyLevel): boolean => {
  const level = getLevelById(id)
  return level?.hasContent ?? false
}

// Helper to determine XP reward tier based on level properties
export const getLevelTier = (
  id: DifficultyLevel,
): 'easy' | 'medium' | 'hard' => {
  const level = getLevelById(id)
  if (!level) return 'easy'

  // Plus levels are always hard
  if (level.isPlus) return 'hard'

  // Group 7/8 are generally harder than 6
  if (level.group >= 7) return 'medium'

  return 'easy'
}

// Get the next level in progression (for level up suggestions)
export const getNextLevel = (
  currentId: DifficultyLevel,
): LevelConfig | undefined => {
  const current = getLevelById(currentId)
  if (!current) return undefined

  // Find levels in the same group that come after this one
  const groupLevels = getLevelsByGroup(current.group)
  const currentIndex = groupLevels.findIndex((l) => l.id === currentId)

  // Try next level in same group
  if (currentIndex < groupLevels.length - 1) {
    const next = groupLevels[currentIndex + 1]
    if (next.hasContent) return next
  }

  // Try first level of next group
  if (current.group < 8) {
    const nextGroup = (current.group + 1) as 6 | 7 | 8
    const nextGroupLevels = getAvailableLevelsByGroup(nextGroup)
    if (nextGroupLevels.length > 0) return nextGroupLevels[0]
  }

  return undefined
}

// Get the previous level in progression (for level down suggestions)
export const getPreviousLevel = (
  currentId: DifficultyLevel,
): LevelConfig | undefined => {
  const current = getLevelById(currentId)
  if (!current) return undefined

  // Find levels in the same group that come before this one
  const groupLevels = getLevelsByGroup(current.group)
  const currentIndex = groupLevels.findIndex((l) => l.id === currentId)

  // Try previous level in same group
  if (currentIndex > 0) {
    const prev = groupLevels[currentIndex - 1]
    if (prev.hasContent) return prev
  }

  // Try last level of previous group
  if (current.group > 6) {
    const prevGroup = (current.group - 1) as 6 | 7 | 8
    const prevGroupLevels = getAvailableLevelsByGroup(prevGroup)
    if (prevGroupLevels.length > 0)
      return prevGroupLevels[prevGroupLevels.length - 1]
  }

  return undefined
}

// ============================================================
// CROSS-GRADE LADDER HELPERS
// ============================================================

/**
 * Get ALL levels as a single ordered array (the continuous difficulty ladder).
 * B6 → B6+ → M6 → M6+ → E6 → E6+ → B7 → ... → E8+
 * This is the foundation for cross-grade adaptive progression.
 */
export const getAllLevelsOrdered = (): LevelConfig[] => {
  return [...LEVELS].sort((a, b) => a.id - b.id)
}

/**
 * Get the 0-based position of a level on the full difficulty ladder.
 * Returns -1 if the level is not found.
 */
export const getLevelPosition = (id: DifficultyLevel): number => {
  return LEVELS.findIndex((l) => l.id === id)
}

/**
 * Get all levels from a starting level upward (inclusive).
 * Used by the adaptive engine to determine the progression range.
 * If toId is provided, stops there (inclusive). Otherwise goes to the end.
 */
export const getLevelsFrom = (
  fromId: DifficultyLevel,
  toId?: DifficultyLevel,
): LevelConfig[] => {
  const all = getAllLevelsOrdered()
  const startIdx = all.findIndex((l) => l.id === fromId)
  if (startIdx === -1) return all // fallback: return everything

  if (toId !== undefined) {
    const endIdx = all.findIndex((l) => l.id === toId)
    if (endIdx === -1) return all.slice(startIdx)
    return all.slice(startIdx, endIdx + 1)
  }

  return all.slice(startIdx)
}

/**
 * Get the full ladder from the beginning up to and including a given level.
 * Used to determine all levels at or below a starting point.
 */
export const getLevelsUpTo = (toId: DifficultyLevel): LevelConfig[] => {
  const all = getAllLevelsOrdered()
  const endIdx = all.findIndex((l) => l.id === toId)
  if (endIdx === -1) return []
  return all.slice(0, endIdx + 1)
}
